
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PropertyImageUpload from './PropertyImageUpload';
import PropertyUniversitySelector from './PropertyUniversitySelector';
import PropertyAmenities from './PropertyAmenities';

interface EditListingFormProps {
  listing: any;
}

const EditListingForm = ({ listing }: EditListingFormProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: listing.title || '',
    description: listing.description || '',
    price: listing.price_egp?.toString() || '',
    propertyType: listing.unit_type || 'apartment',
    furnished: listing.furnished ? 'furnished' : 'unfurnished',
    gender: listing.gender_rules || 'any',
    address: listing.address || '',
    leaseMinMonths: listing.lease_min_months?.toString() || '3',
    deposit: listing.deposit_egp?.toString() || '',
    availableFrom: listing.available_from ? new Date(listing.available_from).toISOString().split('T')[0] : '',
    whatsappNumber: listing.whatsapp_link ? listing.whatsapp_link.replace('https://wa.me/', '').replace(/\D/g, '') : '',
    googleMapsLink: listing.google_maps_link || ''
  });

  const [images, setImages] = useState<string[]>(listing.photos || []);
  const [selectedUniversities, setSelectedUniversities] = useState<string[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>(listing.amenities || []);

  useEffect(() => {
    fetchUniversityRelations();
  }, [listing.id]);

  const fetchUniversityRelations = async () => {
    try {
      const { data, error } = await supabase
        .from('property_universities')
        .select('university_id')
        .eq('listing_id', listing.id);

      if (error) {
        console.error('Error fetching university relations:', error);
        return;
      }

      setSelectedUniversities(data.map(rel => rel.university_id));
    } catch (error) {
      console.error('Exception fetching university relations:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateWhatsAppNumber = (number: string) => {
    if (!number) return true; // Optional field
    // Allow international format starting with + or country codes, or local format
    const phonePattern = /^(\+?[1-9]\d{1,14}|01[0125]\d{8})$/;
    return phonePattern.test(number.replace(/\s/g, ''));
  };

  const validateGoogleMapsLink = (link: string) => {
    if (!link) return true; // Optional field
    // More flexible Google Maps URL validation - accepts various Google Maps formats
    const googleMapsPatterns = [
      /^https:\/\/(www\.)?google\.com\/maps/,
      /^https:\/\/(www\.)?google\.[a-z]{2,3}\/maps/,
      /^https:\/\/maps\.google\.com/,
      /^https:\/\/maps\.google\.[a-z]{2,3}/,
      /^https:\/\/goo\.gl\/maps/,
      /^https:\/\/maps\.app\.goo\.gl/
    ];
    
    return googleMapsPatterns.some(pattern => pattern.test(link));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please sign in to update the property');
      return;
    }

    if (selectedUniversities.length === 0) {
      toast.error('Please select at least one target university');
      return;
    }

    if (images.length === 0) {
      toast.error('Please upload at least one image of the property');
      return;
    }

    // Validate WhatsApp number format
    if (formData.whatsappNumber && !validateWhatsAppNumber(formData.whatsappNumber)) {
      toast.error('Please enter a valid WhatsApp number (e.g., +201234567890 or 01234567890)');
      return;
    }

    // Validate Google Maps link format
    if (formData.googleMapsLink && !validateGoogleMapsLink(formData.googleMapsLink)) {
      toast.error('Please enter a valid Google Maps link (e.g., https://www.google.com/maps/place/...)');
      return;
    }

    setSubmitting(true);

    try {
      console.log('Starting property update...');
      
      // Update the listing
      const listingData = {
        title: formData.title,
        description: formData.description,
        price_egp: parseInt(formData.price),
        deposit_egp: parseInt(formData.deposit),
        address: formData.address,
        unit_type: formData.propertyType,
        furnished: formData.furnished === 'furnished',
        gender_rules: formData.gender,
        lease_min_months: parseInt(formData.leaseMinMonths),
        available_from: formData.availableFrom ? new Date(formData.availableFrom).toISOString() : listing.available_from,
        photos: images,
        amenities: selectedAmenities,
        whatsapp_link: formData.whatsappNumber || null,
        google_maps_link: formData.googleMapsLink || null,
        updated_at: new Date().toISOString(),
        approval_status: 'pending'
      };

      console.log('Updating listing with data:', listingData);

      const { error: listingError } = await supabase
        .from('listings')
        .update(listingData)
        .eq('id', listing.id);

      if (listingError) {
        console.error('Error updating listing:', listingError);
        toast.error(`Failed to update property listing: ${listingError.message}`);
        return;
      }

      console.log('Listing updated successfully, updating university relations...');

      // Update university relationships
      const { error: deleteError } = await supabase
        .from('property_universities')
        .delete()
        .eq('listing_id', listing.id);

      if (deleteError) {
        console.error('Error deleting old university relations:', deleteError);
        toast.error('Property updated but failed to update university relations');
        return;
      }

      if (selectedUniversities.length > 0) {
        const universityRelations = selectedUniversities.map(universityId => ({
          listing_id: listing.id,
          university_id: universityId
        }));

        const { error: relationError } = await supabase
          .from('property_universities')
          .insert(universityRelations);

        if (relationError) {
          console.error('Error updating university relations:', relationError);
          toast.error('Property updated but failed to update some universities');
        }
      }

      toast.success('Property updated successfully! It has been submitted for re-approval.');
      navigate('/my-listings');
      
    } catch (error) {
      console.error('Exception updating listing:', error);
      toast.error('Failed to update property listing');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">
        {t('edit_property', 'تعديل العقار', 'Edit Property')}
      </h1>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Property Images */}
        <PropertyImageUpload
          images={images}
          onImagesChange={setImages}
          maxImages={10}
        />

        {/* Target Universities */}
        <PropertyUniversitySelector
          selectedUniversities={selectedUniversities}
          onUniversitiesChange={setSelectedUniversities}
        />

        {/* Amenities */}
        <PropertyAmenities
          selectedAmenities={selectedAmenities}
          onAmenitiesChange={setSelectedAmenities}
        />

        {/* Basic Information */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold border-b pb-2">
            {t('basic_information', 'المعلومات الأساسية', 'Basic Information')}
          </h2>

          {/* Title */}
          <div className="space-y-2">
            <Label>{t('property_title', 'عنوان العقار', 'Property Title')}</Label>
            <Input
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder={t('enter_title', 'أدخل عنوان العقار', 'Enter property title')}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{t('description', 'الوصف', 'Description')}</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('enter_description', 'أدخل وصف العقار', 'Enter property description')}
              rows={4}
              required
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label>{t('full_address', 'العنوان الكامل', 'Full Address')}</Label>
            <Input
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder={t('enter_full_address', 'أدخل العنوان الكامل', 'Enter full address')}
              required
            />
          </div>
        </div>

        {/* Contact & Location Information */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold border-b pb-2">
            {t('contact_location', 'معلومات التواصل والموقع', 'Contact & Location Information')}
          </h2>

          {/* WhatsApp Number */}
          <div className="space-y-2">
            <Label>{t('whatsapp_number', 'رقم واتساب', 'WhatsApp Number')}</Label>
            <Input
              value={formData.whatsappNumber}
              onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
              placeholder="+201234567890 or 01234567890"
              type="tel"
            />
            <p className="text-sm text-muted-foreground">
              {t('whatsapp_help', 'رقم واتساب للتواصل مع المستأجرين المهتمين', 'WhatsApp number for interested tenants to contact you')}
            </p>
          </div>

          {/* Google Maps Link */}
          <div className="space-y-2">
            <Label>{t('google_maps_link', 'رابط خرائط جوجل', 'Google Maps Link')}</Label>
            <Input
              value={formData.googleMapsLink}
              onChange={(e) => handleInputChange('googleMapsLink', e.target.value)}
              placeholder="https://www.google.com/maps/place/... or https://maps.app.goo.gl/..."
              type="url"
            />
            <p className="text-sm text-muted-foreground">
              {t('maps_help', 'رابط خرائط جوجل لموقع العقار - يمكن استخدام أي صيغة من روابط خرائط جوجل', 'Google Maps link to show property location - any Google Maps URL format is accepted')}
            </p>
          </div>
        </div>

        {/* Property Details */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold border-b pb-2">
            {t('property_details', 'تفاصيل العقار', 'Property Details')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Property Type */}
            <div className="space-y-2">
              <Label>{t('property_type', 'نوع العقار', 'Property Type')}</Label>
              <Select value={formData.propertyType} onValueChange={(value) => handleInputChange('propertyType', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">{t('apartment', 'شقة', 'Apartment')}</SelectItem>
                  <SelectItem value="room">{t('room', 'غرفة', 'Room')}</SelectItem>
                  <SelectItem value="studio">{t('studio', 'استوديو', 'Studio')}</SelectItem>
                  <SelectItem value="house">{t('house', 'منزل', 'House')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Furnished */}
            <div className="space-y-2">
              <Label>{t('furnished_status', 'حالة الأثاث', 'Furnished Status')}</Label>
              <Select value={formData.furnished} onValueChange={(value) => handleInputChange('furnished', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="furnished">{t('furnished', 'مفروش', 'Furnished')}</SelectItem>
                  <SelectItem value="unfurnished">{t('unfurnished', 'غير مفروش', 'Unfurnished')}</SelectItem>
                  <SelectItem value="semi_furnished">{t('semi_furnished', 'مفروش جزئياً', 'Semi Furnished')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Rental Information */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold border-b pb-2">
            {t('rental_information', 'معلومات الإيجار', 'Rental Information')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Monthly Rent */}
            <div className="space-y-2">
              <Label>{t('monthly_rent', 'الإيجار الشهري', 'Monthly Rent')}</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="1"
                  required
                  className="pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-muted-foreground text-sm">EGP</span>
                </div>
              </div>
            </div>

            {/* Deposit */}
            <div className="space-y-2">
              <Label>{t('security_deposit', 'التأمين', 'Security Deposit')}</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={formData.deposit}
                  onChange={(e) => handleInputChange('deposit', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="1"
                  required
                  className="pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-muted-foreground text-sm">EGP</span>
                </div>
              </div>
            </div>

            {/* Minimum Lease */}
            <div className="space-y-2">
              <Label>{t('minimum_lease_months', 'أقل مدة إيجار (شهور)', 'Minimum Lease (Months)')}</Label>
              <Input
                type="number"
                value={formData.leaseMinMonths}
                onChange={(e) => handleInputChange('leaseMinMonths', e.target.value)}
                placeholder="3"
                min="1"
                step="1"
                required
              />
            </div>

            {/* Available From */}
            <div className="space-y-2">
              <Label>{t('available_from', 'متاح من', 'Available From')}</Label>
              <Input
                type="date"
                value={formData.availableFrom}
                onChange={(e) => handleInputChange('availableFrom', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold border-b pb-2">
            {t('tenant_preferences', 'تفضيلات المستأجر', 'Tenant Preferences')}
          </h2>

          {/* Gender Preference */}
          <div className="space-y-2">
            <Label>{t('gender_preference', 'تفضيل الجنس', 'Gender Preference')}</Label>
            <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t('any_gender', 'أي جنس', 'Any')}</SelectItem>
                <SelectItem value="female">{t('female_only', 'إناث فقط', 'Female only')}</SelectItem>
                <SelectItem value="male">{t('male_only', 'ذكور فقط', 'Male only')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/my-listings')}
            className="flex-1"
          >
            {t('cancel', 'إلغاء', 'Cancel')}
          </Button>
          <Button type="submit" className="flex-1" disabled={submitting}>
            {submitting ? t('updating', 'جاري التحديث...', 'Updating...') : t('update_property', 'تحديث العقار', 'Update Property')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditListingForm;
